import { DashboardState } from '../objects/dashboardState';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, addYears, differenceInCalendarDays } from 'date-fns';

const initialState: DashboardState = {
    all_data: null,
    all_providers_data: null,
    all_quotes_data: null,
    start_date: localStorage.getItem('dashboard_start_date') ? new Date(JSON.parse(localStorage.getItem('dashboard_start_date'))) : addYears(new Date(), -1),
    end_date: localStorage.getItem('dashboard_end_date') ? new Date(JSON.parse(localStorage.getItem('dashboard_end_date'))) : new Date(),
    compare: localStorage.getItem('dashboard_compare') ? JSON.parse(localStorage.getItem('dashboard_compare')) : false,
    compare_option: localStorage.getItem('dashboard_compare_option') ? JSON.parse(localStorage.getItem('dashboard_compare_option')) : 0,
    compare_start_date: localStorage.getItem('dashboard_compare_start_date') ? new Date(JSON.parse(localStorage.getItem('dashboard_compare_start_date'))) : addYears(new Date(), -2),
    compare_end_date: localStorage.getItem('dashboard_compare_end_date') ? new Date(JSON.parse(localStorage.getItem('dashboard_compare_end_date'))) : addYears(new Date(), -1),
    period_option: 0,
    computed_data: null,
    computed_providers_data: null,
    gdt: 'sd',
    sat: 'all', // Selected Agent
    sp: 'all', // Selected Provider
    say: 'all', // Selected Agency
    tredt: 'sd', // Total Revenue Date type
    tmadt: 'sd', // Total Margin Date type
    cdtt: 're', // Country Data Type
    cdt: 'sd', // Country Date Type
    cresc: '', // Country selected country
    credt: 'sd', // Country Revenue Date Type
    credf: 'w', // Country Revenue Date Format
    cred: [], // Country Revenue Data
    repadt: 'sd', // Revenue Per Agent Date Type
    repadf: 'w', // Revenue Per Agent Date Format
    repad: [], // Revenue Per Agent Data
    mapadt: 'sd', // Margin Per Agent Date Type
    mapadf: 'w', // Margin Per Agent Date Format
    mapad: [], // Margin Per Agent Data
    t10repadt: 'sd', // Top 10 Revenue Per Agent Date Type
    t10repad: [], // Top 10 Revenue Per Agent Data
    t10mapadt: 'sd', // Top 10 Margin Per Agent Date Type
    t10mapad: [], // Top 10 Margin Per Agent Data
    redt: 'sd', // Revenue Date Type
    redf: 'w', // Revenue Date Format
    red: [], // Revenue Data
    madt: 'sd', // Margin Date Type
    madf: 'w', // Margin Date Format
    mad: [], // Margin Data
    mrdt: 'sd', // Margin Rate Date Type
    mrdf: 'w', // Margin Rate Date Format
    mrd: [], // Margin Rate Data
    crdf: 'w', // Cash Receipt Date Format
    crd: [], // Cash Receipt Data
    nqsdf: 'w', // New Quotation Sent Date Format
    nqsd: [], // New Quotation Sent Data
    msdf: 'w', // Modification Sent Date Format
    msd: [], // Modification Sent Data
    nofsdf: 'w', // Number Of First Signature Date Format
    nofsd: [], // Number Of First Signature Data
    nofcdf: 'w', // Number Of First Confirmation Date Format
    nofcd: [], // Number Of First Confirmation Data
    fwidt: 'sd', // File With Insurance Date Type
    fwidf: 'w', // File With Insurance Date Format
    fwid: [], // File With Insurance Data
    isrdt: 'sd', // Insurance Sales Rate Date Type
    isrdf: 'w', // Insurance Sales Rate Date Format
    isrd: [], // Insurance Sales Rate Data
    aarpfdt: 'sd',
    aarpfdf: 'w',
    aarpfd: [],
    aampfdt: 'sd',
    aampfdf: 'w',
    aampfd: [],
    anqsdf: 'w',
    anqsd: [],
    amsdf: 'w',
    amsd: [],
    anofsdf: 'w',
    anofsd: [],
    anofcdf: 'w',
    anofcd: [],
    adt: 'sd', // Agent Date Type
    ad: [], // Agent Data Table
    aydt: 'sd', // Agency Date Type
    ayd: [],
    ayrdt: 'sd',
    ayrdf: 'w',
    ayrd: [],
    aymdt: 'sd',
    aymdf: 'w',
    aymd: [],
    aynqsdf: 'w',
    aynqsd: [],
    aymsdf: 'w',
    aymsd: [],
    aynofcdf: 'w',
    aynofcd: [],
    reppdf: 'w', // Provider Revenue Date Format
    mappdf: 'w', // Provider Margin Date Format
    nonppdf: 'w', // Provider Number of Night Date Format
    anoindf: 'w', // Agent number of invoice Date Format
    noindf: 'w', // Number of invoice Date Format
    anoind: [], // Agent number of invoice Data
    noind: [], // Number of invoice Data
    nonpddt: 'sd', // Number Of Nights Per Destination Date Type
    nonpdd: [], // Number Of Nights Per Destination Data
    nonpcdt: 'sd', // Number Of Nights Per Country Date Type
    nonpcd: [], // Number Of Nights Per Country Data
    computed_quotes_data: null, // Quotes computation shown next to computed_data (agencies dashboard)
    ayqdt: 'sd', // Agency Quotes Date Type
    ayqd: [], // Agency Quotes Data Table
    hbstd: [], // Hotels By Stars Total Data
    hbssd: [], // Hotels By Stars Per Step Data
    hbscd: [], // Hotels By Stars Per Country Data
    hbmtd: [], // Hotels By Meal plan Total Data
    hbmsd: [], // Hotels By Meal plan Per Step Data
    hbmcd: [], // Hotels By Meal plan Per Country Data
    mucdt: 'sd', // Most Used Circuits Date Type
    mucd: [], // Most Used Circuits Data
    muitdt: 'sd', // Most Used Itinerary Types Date Type
    muitd: [], // Most Used Itinerary Types Data
    tsdt: 'sd', // Trip Sources (tailor made / circuit / itinerary type) Date Type
    tsd: [], // Trip Sources Data
    tnoqdt: 'sd', // Total Number Of Quotes Date Type
    tatcdt: 'sd', // Total Average Time To Confirm Date Type
    ditdt: 'sd', // Destinations In Trips Date Type
    ditd: [], // Destinations In Trips Data
    thd: [], // Top Hotels sold Data
    nspdd: [], // Nights Sold Per Destination Data
};

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setAllData: (state, action: PayloadAction<any>) => {
            state.all_data = action.payload;
        },
        setAllProvidersData: (state, action: PayloadAction<any>) => {
            state.all_providers_data = action.payload;
        },
        setAllQuotesData: (state, action: PayloadAction<any>) => {
            state.all_quotes_data = action.payload;
        },
        updateDates: (state, action: PayloadAction<{start_date: Date, end_date: Date, compare: boolean, compare_option: number}>) => {
            state.start_date = action.payload.start_date;
            state.end_date = action.payload.end_date;
            state.compare = action.payload.compare;
            state.compare_option = action.payload.compare_option;
            if (state.compare_option === 1) {
                state.compare_start_date = addYears(action.payload.start_date, -1);
                state.compare_end_date = addYears(action.payload.end_date, -1);
            } else {
                state.compare_start_date = addDays(action.payload.start_date, -1 + differenceInCalendarDays(action.payload.start_date, action.payload.end_date));
                state.compare_end_date = addDays(action.payload.start_date, -1);
            }
            localStorage.setItem('dashboard_start_date', JSON.stringify(state.start_date));
            localStorage.setItem('dashboard_end_date', JSON.stringify(state.end_date));
            localStorage.setItem('dashboard_compare_start_date', JSON.stringify(state.compare_start_date));
            localStorage.setItem('dashboard_compare_end_date', JSON.stringify(state.compare_end_date));
            localStorage.setItem('dashboard_compare_option', JSON.stringify(state.compare_option));
            localStorage.setItem('dashboard_compare', JSON.stringify(state.compare));
        },
        updatePeriodOption: (state, action: PayloadAction<number>) => {
            state.period_option = action.payload;
        },
        updateDateType: (state, action: PayloadAction<"sd" | "cd">) => {
            state.gdt = action.payload;
            state.tredt = action.payload;
            state.tmadt = action.payload;
            state.cdt = action.payload;
            state.credt = action.payload;
            state.repadt = action.payload;
            state.mapadt = action.payload;
            state.t10repadt = action.payload;
            state.t10mapadt = action.payload;
            state.redt = action.payload;
            state.madt = action.payload;
            state.mrdt = action.payload;
            state.fwidt = action.payload;
            state.isrdt = action.payload;
            state.aarpfdt = action.payload;
            state.aampfdt = action.payload;
            state.adt = action.payload;
            state.aydt = action.payload;
            state. ayrdt = action.payload;
            state.aymdt = action.payload;
            state.nonpddt = action.payload;
            state.nonpcdt = action.payload;
            state.mucdt = action.payload;
            state.muitdt = action.payload;
            state.tsdt = action.payload;
            state.tnoqdt = action.payload;
            state.tatcdt = action.payload;
            state.ditdt = action.payload;
        },
        setDashboardFields: (state, action) => {
            const { key, value } = action.payload;
            if (key === 'sd') {
                state.start_date = new Date(value);
            } else if (key === 'ed') {
                state.end_date = new Date(value);
            } else if (key === 'c') {
                state.compare = Boolean(value);
            } else if (key === 'csd') {
                state.compare_start_date = new Date(value);
            } else if (key === 'ced') {
                state.compare_end_date = new Date(value);
            }
            state[key] = key === "cresc" ? decodeURI(value) : value;
        },
        setComputedData: (state, action: PayloadAction<any>) => {
            state.computed_data = action.payload;
        },
        setComputedProvidersData: (state, action: PayloadAction<any>) => {
            state.computed_providers_data = action.payload;
        },
        setComputedQuotesData: (state, action: PayloadAction<any>) => {
            state.computed_quotes_data = action.payload;
        }
    }
});

export const {
    setAllData,
    setAllProvidersData,
    setAllQuotesData,
    updateDates,
    updatePeriodOption,
    updateDateType,
    setDashboardFields,
    setComputedData,
    setComputedProvidersData,
    setComputedQuotesData,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;